import React, { useCallback, useState } from 'react'
import { FormikContextType } from 'formik/dist/types'
import { useTranslation } from 'react-i18next'
import { FieldArray } from 'formik'
import styles from './LearningPathForm.module.scss'
import Button from '../basics/Button/Button'
import { SelectLearningItemFormik } from '../formElements/SelectLearningItem/SelectLearningItem'
import { AnonymousLearningItem } from '../../models/AnonymousLearningItem'
import RemoveIcon from '@material-symbols/svg-300/rounded/remove.svg'
import KeyboardArrUp from '@material-symbols/svg-300/rounded/arrow_upward_alt.svg'
import KeyboardArrDown from '@material-symbols/svg-300/rounded/arrow_downward_alt.svg'
import { Link } from 'react-router-dom'
import LearningItemImage from '../LearningItemImage/LearningItemImage'
import useRoutes from '../../state/useRoutes'
import { useLearningItem } from '../../state/entities'
import FieldError from '../basics/FieldError/FieldError'
import classNames from 'classnames'

export type LearningPathCoursesValues = {
  learningItemUuid: string | null
  courses: AnonymousLearningItem[] | []
}

export default React.memo(function LearningPathCoursesForm({
  formik,
}: {
  formik: FormikContextType<LearningPathCoursesValues>
}) {
  const [toggleCourseSelection, setToggleCourseSelection] = useState<boolean>(!formik.values.courses?.length)
  const { t } = useTranslation()
  const routes = useRoutes()

  const alreadyAddedCourse = useCallback((learningItemUuid: string, courses: AnonymousLearningItem[]) => {
    return courses?.some((course) => course.uuid === learningItemUuid) || false
  }, [])

  const mapLearningItemSuggestions = useCallback(
    (item: AnonymousLearningItem) => {
      if (item.kind === 'learningPath') {
        return {
          disabled: true,
          info: t('learning item.type.learning_path'),
        }
      }

      if (item.status !== 'published') {
        return {
          disabled: true,
          info: t(`learning item.status.${item.status}`),
        }
      }

      if (alreadyAddedCourse(item.uuid, formik.values.courses || [])) {
        return {
          disabled: true,
          info: t(`learning path.already added`),
        }
      }

      return { disabled: false }
    },
    [formik.values.courses, formik.values.learningItemUuid]
  )

  const { learningItem: learningItemToAdd } = useLearningItem(formik.values.learningItemUuid)

  const onAddCourse = useCallback(() => {
    if (alreadyAddedCourse(formik.values.learningItemUuid!, formik.values.courses)) {
      return
    }

    formik
      .setFieldValue('courses', [...(formik.values.courses || []), learningItemToAdd])
      .then(() => {
        // console.log('formik.values.courses after', formik.values.courses)
      })
      .catch(() => {
        // ( error ) => {
        // console.error('formik.setFieldValue error', error)
      })
  }, [formik.values.learningItemUuid, learningItemToAdd])

  return (
    <>
      <FieldArray
        name="courses"
        render={(arrayHelpers) => (
          <section className={styles.courses}>
            <header>
              <h2>{t('learning path.courses list title')}</h2>

              <Button small filled onClick={() => setToggleCourseSelection(!toggleCourseSelection)}>
                {t('edit learning.cta select course to add')}
              </Button>
            </header>

            {toggleCourseSelection && (
              <div className={styles.addCourse}>
                <SelectLearningItemFormik name="learningItemUuid" mapSuggestion={mapLearningItemSuggestions} />

                <div className={styles.addButton}>
                  <Button small filled disabled={!formik.values.learningItemUuid} onClick={onAddCourse}>
                    {t('edit learning.cta add course')}
                  </Button>
                </div>
              </div>
            )}

            <main className={classNames({ [styles.errorBorder]: !!formik.errors.courses })}>
              {formik.values.courses &&
                formik.values.courses.map((course, idx) => (
                  <div key={idx} className={styles.CourseLearningPathLearningItemEdit}>
                    <span>
                      <Link to={routes.course.url({ uuid: course.uuid })}>
                        <LearningItemImage learningItem={course} className={styles.coverListImage} />
                      </Link>
                    </span>
                    <span>{course.title}</span>
                    <span className={styles.courseActions}>
                      <Button small onClick={() => arrayHelpers.remove(idx)}>
                        <RemoveIcon />
                      </Button>
                      <Button
                        small
                        onClick={() => arrayHelpers.move(idx, idx + 1)}
                        disabled={idx === formik.values.courses.length - 1}
                      >
                        <KeyboardArrDown />
                      </Button>
                      <Button small onClick={() => arrayHelpers.move(idx, idx - 1)} disabled={idx === 0}>
                        <KeyboardArrUp />
                      </Button>
                    </span>
                  </div>
                ))}

              {formik.values.courses && formik.values.courses.length === 0 && (
                <div className={styles.emptyList}>
                  {t('learning path.no learning items')}
                  <FieldError error={formik.errors.courses as string} touched={!!formik.errors.courses} />
                </div>
              )}
            </main>
          </section>
        )}
      />
    </>
  )
})
